<script setup lang="ts">
/**
 * Мониторинг пайплайнов — макет 20:2: полоса показателей, переключатель
 * группировки и сворачиваемые группы прогонов.
 *
 * Сводка привязана к проекту трекера: эпик строки считается по иерархии задач,
 * а она живёт внутри проекта. Поэтому вверху стоит выбор проекта, а не «все
 * сразу»; выбранный проект остаётся в адресе, чтобы ссылкой на экран можно было
 * поделиться.
 *
 * Экран обновляется сам, пока на нём есть бегущий прогон: смотреть на монитор и
 * жать «обновить» — это работа за компьютер. Когда всё доиграло, опрос
 * останавливается: тратить запросы на неподвижную картинку незачем.
 */
import { AppButton, AppSegmentedControl, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, GitBranch, RefreshCw, Search } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import {
  formatDuration,
  getSummary,
  groupRows,
  isLive,
  listRecent,
  toKpi,
  type GroupBy,
  type PipelineRun,
  type SummaryRow,
} from '@/features/pipelines/api'
import PipelineGroupCard from '@/features/pipelines/components/PipelineGroupCard.vue'
import DevOpsMetric from '@/features/devops/components/DevOpsMetric.vue'
import { formatStamp } from '@/features/issues/format'
import { useProjectsStore } from '@/features/projects/store'

/** Как часто перечитывать, пока что-то бежит. Полминуты — шаг вебхуков GitLab. */
const REFRESH_MS = 30_000

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const projects = useProjectsStore()

const rows = ref<SummaryRow[]>([])
const runs = ref<PipelineRun[]>([])
const isLoading = ref(false)
const error = ref('')
const groupBy = ref<GroupBy>('epic')
const search = ref('')
const statusFilter = ref<string[]>([])
const windowFilter = ref<string[]>([])
const collapsed = ref(new Set<string>())

let timer: ReturnType<typeof setInterval> | undefined

const projectKey = computed({
  get: () => String(route.query.project ?? projects.items[0]?.key ?? ''),
  set: (key: string) => void router.replace({ query: { ...route.query, project: key } }),
})

const project = computed(() => projects.items.find((entry) => entry.key === projectKey.value))

const projectOptions = computed(() =>
  projects.items.map((entry) => ({ value: entry.key, label: `${entry.name} · ${entry.key}` })),
)

const statusOptions = computed(() =>
  [...new Set(rows.value.map((row) => row.run.status))].map((status) => ({
    value: status,
    label: status,
  })),
)

/** Окно свежести из макета: сутки и неделя. Пусто — показываем всё. */
const windowOptions = computed(() =>
  ['1', '7'].map((days) => ({
    value: days,
    label: t('devops.pipelines.windowDays', { days: Number(days) }),
  })),
)

const groupOptions = computed(() => [
  { value: 'epic' as const, label: t('devops.pipelines.byEpic') },
  { value: 'service' as const, label: t('devops.pipelines.byService') },
])

const visible = computed(() => {
  const needle = search.value.trim().toLowerCase()

  const days = windowFilter.value.length
    ? Math.max(...windowFilter.value.map((value) => Number(value)))
    : 0
  const since = days ? Date.now() - days * 86_400_000 : 0

  return rows.value.filter((row) => {
    if (statusFilter.value.length && !statusFilter.value.includes(row.run.status)) return false
    if (since && new Date(row.run.createdAt).getTime() < since) return false
    if (!needle) return true

    return [row.run.projectPath, row.run.ref, row.issueKey, row.issueTitle]
      .join(' ')
      .toLowerCase()
      .includes(needle)
  })
})

const groups = computed(() => groupRows(visible.value, groupBy.value))

const kpi = computed(() => toKpi(runs.value))

/** Дельта со знаком: «−18» читается как «стало быстрее», «18» — никак. */
const signed = (seconds: number) => (seconds > 0 ? `+${seconds}` : `−${Math.abs(seconds)}`)

const load = async () => {
  if (!project.value) return

  isLoading.value = true
  error.value = ''

  try {
    const [summary, recent] = await Promise.all([getSummary(project.value.id), listRecent()])

    rows.value = summary
    runs.value = recent
  } catch {
    error.value = t('devops.pipelines.error')
  } finally {
    isLoading.value = false
  }
}

/** Опрос живёт, только пока есть что смотреть. */
const syncTimer = () => {
  const live = rows.value.some((row) => isLive(row.run))

  if (live && !timer) timer = setInterval(load, REFRESH_MS)
  if (!live && timer) {
    clearInterval(timer)
    timer = undefined
  }
}

watch(rows, syncTimer)
watch(project, load)

const stamp = (iso: string) =>
  formatStamp(iso, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

const toggle = (key: string) => {
  const next = new Set(collapsed.value)

  if (next.has(key)) next.delete(key)
  else next.add(key)

  collapsed.value = next
}

onMounted(async () => {
  // Список проектов нужен раньше сводки: в ней проект — обязательный довод.
  await projects.ensureLoaded()
  await load()
})

onBeforeUnmount(() => clearInterval(timer))
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[
      { label: t('nav.devops'), to: '/devops' },
      { label: t('devops.pipelines.title') },
    ]"
  >
    <FilterChip
      :model-value="projectKey ? [projectKey] : []"
      :label="t('devops.pipelines.project')"
      :options="projectOptions"
      :empty-text="t('devops.pipelines.noProjects')"
      @update:model-value="projectKey = $event[$event.length - 1] ?? ''"
    />
    <FilterChip
      v-model="statusFilter"
      :label="t('devops.pipelines.status')"
      :options="statusOptions"
      :empty-text="t('devops.pipelines.noProjects')"
    />

    <FilterChip
      v-model="windowFilter"
      :label="t('devops.pipelines.window')"
      :options="windowOptions"
      :empty-text="t('devops.pipelines.noProjects')"
    />

    <button
      type="button"
      class="pipes__refresh"
      :aria-label="t('devops.envs.refresh')"
      :title="t('devops.envs.refresh')"
      @click="load"
    >
      <RefreshCw :size="15" />
    </button>

    <div class="pipes__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('devops.pipelines.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('devops.pipelines.searchPlaceholder')"
      />
    </div>
  </TrackerToolbar>

  <div class="pipes">
    <div class="pipes__kpi">
      <DevOpsMetric
        :label="t('devops.pipelines.kpi.success')"
        :value="kpi.successful"
        :hint="t('devops.pipelines.kpi.successHint', { total: kpi.finished })"
        tone="success"
      />
      <DevOpsMetric
        :label="t('devops.pipelines.kpi.failed')"
        :value="kpi.failed"
        :hint="kpi.failedServices.map((path) => path.split('/').pop()).join(' · ')"
        tone="warning"
      />
      <DevOpsMetric
        :label="t('devops.pipelines.kpi.running')"
        :value="kpi.running"
        :hint="kpi.runningServices.map((path) => path.split('/').pop()).join(' · ')"
        tone="info"
      />
      <DevOpsMetric
        :label="t('devops.pipelines.kpi.duration')"
        :value="formatDuration(kpi.averageSeconds) || '—'"
        :hint="
          kpi.averageDeltaSeconds
            ? t('devops.pipelines.kpi.delta', { delta: signed(kpi.averageDeltaSeconds) })
            : ''
        "
        tone="primary"
      />
    </div>

    <AppSegmentedControl
      v-model="groupBy"
      :options="groupOptions"
      size="small"
      :aria-label="t('devops.pipelines.groupBy')"
    />

    <div v-if="isLoading && !rows.length" class="pipes__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('devops.pipelines.error')"
      :description="t('devops.pipelines.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!groups.length"
      :icon="GitBranch"
      :title="t('devops.pipelines.empty.title')"
      :description="t('devops.pipelines.empty.description')"
    />

    <PipelineGroupCard
      v-for="group in groups"
      :key="group.key"
      :group="group"
      :open="!collapsed.has(group.key)"
      :stamp="stamp"
      @toggle="toggle(group.key)"
    />
  </div>
</template>

<style scoped>
.pipes {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  padding: 24px 48px;
}

.pipes__kpi {
  display: flex;
  gap: 12px;
}

.pipes__skeleton {
  height: 320px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.pipes__search {
  width: 220px;
}

.pipes__refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.pipes__refresh:hover {
  color: var(--color-text-primary);
}
</style>
